import {
    ActivityCategory,
    ActivityRecordType,
    asActivityCategory,
    asActivityRecordTypes,
    TaskTypeId,
} from "@sp-crm/core";
import { AdvancedSearchEntityType, useGetTaskTypesQuery } from "generated/graphql";
import { useMemo } from "react";
import { stableQueryOptions } from "util/requests";

export interface ActivityTypeOption {
    id: TaskTypeId;
    name: string;
    category: ActivityCategory;
    isArchived: boolean;
    recordTypes: ActivityRecordType[];
}

export const noActivityTypeKey = "";

export const chooseActivityTypeLabel = "Choose an activity type";

export const byActivityTypeName = (
    a: ActivityTypeOption,
    b: ActivityTypeOption,
): number => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());

const categoryChoices = (
    options: ActivityTypeOption[],
    category: ActivityCategory,
    selectedTypeId: TaskTypeId | null,
): ActivityTypeOption[] =>
    options.filter(
        option =>
            option.category === category &&
            (!option.isArchived || option.id === selectedTypeId),
    );

export const activityTypeChoicesForRecordType = (
    options: ActivityTypeOption[],
    category: ActivityCategory,
    recordType: ActivityRecordType | null,
    selectedTypeId: TaskTypeId | null,
): ActivityTypeOption[] =>
    categoryChoices(options, category, selectedTypeId).filter(
        option =>
            recordType === null ||
            option.recordTypes.includes(recordType) ||
            option.id === selectedTypeId,
    );

export const recordTypeActivityChoices = (
    options: ActivityTypeOption[],
    recordType: ActivityRecordType | null,
    selectedTypeId: TaskTypeId | null,
): ActivityTypeOption[] =>
    options
        .filter(
            option =>
                (!option.isArchived || option.id === selectedTypeId) &&
                (recordType === null ||
                    option.recordTypes.includes(recordType) ||
                    option.id === selectedTypeId),
        )
        .sort(byActivityTypeName);

const referralSourceRecordTypes: ActivityRecordType[] = [
    "referenceContact",
    "referenceBusiness",
];

export const isReferralSourceRecordType = (recordType: ActivityRecordType): boolean =>
    referralSourceRecordTypes.includes(recordType);

export const unscopedVisitTypeChoices = (
    options: ActivityTypeOption[],
    selectedTypeId: TaskTypeId | null,
): ActivityTypeOption[] =>
    categoryChoices(options, "visit", selectedTypeId).filter(
        option =>
            option.recordTypes.some(
                recordType => !isReferralSourceRecordType(recordType),
            ) || option.id === selectedTypeId,
    );

export const activityTypesForRecordType = (
    options: ActivityTypeOption[],
    recordType: ActivityRecordType,
    keepTypeIds: TaskTypeId[],
): ActivityTypeOption[] =>
    options.filter(
        option =>
            option.recordTypes.includes(recordType) || keepTypeIds.includes(option.id),
    );

const isAdvancedSearchEntityType = (
    entityKind: string,
): entityKind is AdvancedSearchEntityType =>
    (Object.values(AdvancedSearchEntityType) as string[]).includes(entityKind);

const activityRecordTypeForEntityType = (
    entityType: AdvancedSearchEntityType,
): ActivityRecordType | null => {
    switch (entityType) {
        case AdvancedSearchEntityType.Client:
            return "placement";
        case AdvancedSearchEntityType.HomeCareLead:
            return "homeCare";
        case AdvancedSearchEntityType.Community:
            return "community";
        case AdvancedSearchEntityType.ReferenceContact:
            return "referenceContact";
        case AdvancedSearchEntityType.ReferenceBusiness:
            return "referenceBusiness";
        case AdvancedSearchEntityType.Activity:
        case AdvancedSearchEntityType.CalendarEvent:
        case AdvancedSearchEntityType.CommunityContact:
        case AdvancedSearchEntityType.Invoice:
        case AdvancedSearchEntityType.Task:
        case AdvancedSearchEntityType.Tour:
        case AdvancedSearchEntityType.WorkflowExecution:
            return null;
    }
    const exhaustiveCheck: never = entityType;
    throw new Error(`Unhandled entity type ${String(exhaustiveCheck)}`);
};

export const activityRecordTypeForEntityKind = (
    entityKind: string,
): ActivityRecordType | null =>
    isAdvancedSearchEntityType(entityKind)
        ? activityRecordTypeForEntityType(entityKind)
        : null;

export interface ActivityTypeOptionsState {
    options: ActivityTypeOption[];
    isLoading: boolean;
}

export const useActivityTypeOptionsState = (): ActivityTypeOptionsState => {
    const query = useGetTaskTypesQuery({}, stableQueryOptions());
    return useMemo(
        () => ({
            options: (query.data?.getTaskTypes ?? []).map(taskType => ({
                id: taskType.id,
                name: taskType.name,
                category: asActivityCategory(taskType.category),
                isArchived: taskType.isArchived,
                recordTypes: asActivityRecordTypes(taskType.recordTypes),
            })),
            isLoading: query.isLoading,
        }),
        [query.data?.getTaskTypes, query.isLoading],
    );
};

export const useActivityTypeOptions = (): ActivityTypeOption[] =>
    useActivityTypeOptionsState().options;

export const defaultActivityTypeId = (
    options: ActivityTypeOption[],
    category: ActivityCategory,
    recordType: ActivityRecordType | null,
): TaskTypeId | null => {
    const applicableTypes = activityTypeChoicesForRecordType(
        options,
        category,
        recordType,
        null,
    );
    return applicableTypes.length === 1 ? applicableTypes[0].id : null;
};

export const useDefaultActivityTypeId = (
    category: ActivityCategory,
    recordType: ActivityRecordType | null,
): TaskTypeId | null =>
    defaultActivityTypeId(useActivityTypeOptions(), category, recordType);
